/**
 * Pure validation for Touch Panel deployable payloads (Phase 4).
 */

import {
  PANEL_ACTION_OPEN_SCREEN,
  PANEL_ACTION_HOME,
  PANEL_ACTION_BACK,
  PANEL_ACTION_NONE,
  PANEL_ACTION_FLOW,
} from '../../pages/Admin/TouchPanelDesigner/panelModel';
import { TOUCH_PANEL_PAYLOAD_LIMITS, isFirmwareSupportedIconKey } from './payloadLimits';
import { measurePayloadBytes, summarizePayload } from './serializeDeployablePanel';

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function issue(code, path, message, level = 'error') {
  return { code, path, message, level };
}

function isValidColor(value) {
  if (value == null || value === '') return true;
  return HEX_COLOR_RE.test(String(value));
}

/**
 * Validate a deployable payload, optionally against a device snapshot.
 * @returns {{ valid: boolean, errors: Array, warnings: Array, summary: object }}
 */
export function validateDeployablePanel(payload, { device = null } = {}) {
  const errors = [];
  const warnings = [];
  const limits = TOUCH_PANEL_PAYLOAD_LIMITS;

  if (!payload || typeof payload !== 'object') {
    return {
      valid: false,
      errors: [issue('PAYLOAD_MISSING', '', 'Deployable payload is missing.')],
      warnings: [],
      summary: null,
    };
  }

  const schemaVersion = Number(payload.schemaVersion);
  if (!limits.SUPPORTED_SCHEMA_VERSIONS.includes(schemaVersion)) {
    errors.push(
      issue(
        'UNSUPPORTED_SCHEMA',
        'schemaVersion',
        `Unsupported schema version ${payload.schemaVersion}. Supported: ${limits.SUPPORTED_SCHEMA_VERSIONS.join(', ')}.`
      )
    );
  }

  const panel = payload.panel;
  if (!panel) {
    errors.push(issue('PANEL_MISSING', 'panel', 'panel object is required.'));
    return { valid: false, errors, warnings, summary: null };
  }

  if (!panel.id) {
    errors.push(issue('PANEL_ID_MISSING', 'panel.id', 'Panel id is required.'));
  }

  const screens = Array.isArray(panel.screens) ? panel.screens : [];
  if (screens.length === 0) {
    errors.push(issue('SCREENS_EMPTY', 'panel.screens', 'At least one screen is required.'));
  }
  if (screens.length > limits.MAX_SCREENS) {
    errors.push(
      issue(
        'TOO_MANY_SCREENS',
        'panel.screens',
        `Panel has ${screens.length} screens; max is ${limits.MAX_SCREENS} (provisional).`
      )
    );
  }

  const screenIds = new Set();
  screens.forEach((screen, si) => {
    const sp = `panel.screens[${si}]`;
    if (!screen?.id) {
      errors.push(issue('SCREEN_ID_MISSING', `${sp}.id`, 'Screen id is required.'));
    } else if (screenIds.has(screen.id)) {
      errors.push(issue('DUPLICATE_SCREEN_ID', `${sp}.id`, `Duplicate screen id “${screen.id}”.`));
    } else {
      screenIds.add(screen.id);
    }

    const rows = Number(screen.rows);
    const cols = Number(screen.columns);
    if (!Number.isInteger(rows) || rows < limits.MIN_ROWS || rows > limits.MAX_ROWS) {
      errors.push(
        issue(
          'INVALID_ROWS',
          `${sp}.rows`,
          `rows must be an integer ${limits.MIN_ROWS}–${limits.MAX_ROWS}.`
        )
      );
    }
    if (!Number.isInteger(cols) || cols < limits.MIN_COLUMNS || cols > limits.MAX_COLUMNS) {
      errors.push(
        issue(
          'INVALID_COLUMNS',
          `${sp}.columns`,
          `columns must be an integer ${limits.MIN_COLUMNS}–${limits.MAX_COLUMNS}.`
        )
      );
    }

    const maxSlots =
      (Number.isInteger(rows) ? rows : 0) * (Number.isInteger(cols) ? cols : 0);
    const buttons = Array.isArray(screen.buttons) ? screen.buttons : [];
    if (buttons.length > limits.MAX_BUTTONS_PER_SCREEN) {
      errors.push(
        issue(
          'TOO_MANY_BUTTONS_ON_SCREEN',
          `${sp}.buttons`,
          `Screen has ${buttons.length} buttons; max is ${limits.MAX_BUTTONS_PER_SCREEN} (provisional).`
        )
      );
    }

    const positions = new Set();
    buttons.forEach((btn, bi) => {
      const bp = `${sp}.buttons[${bi}]`;
      if (!btn?.id) {
        errors.push(issue('BUTTON_ID_MISSING', `${bp}.id`, 'Button id is required.'));
      }
      const pos = Number(btn.position);
      if (!Number.isInteger(pos) || pos < 0 || (maxSlots > 0 && pos >= maxSlots)) {
        errors.push(
          issue(
            'INVALID_BUTTON_POSITION',
            `${bp}.position`,
            `Button position ${btn.position} is out of range for ${cols}×${rows}.`
          )
        );
      } else if (positions.has(pos)) {
        errors.push(
          issue(
            'DUPLICATE_BUTTON_POSITION',
            `${bp}.position`,
            `Duplicate button position ${pos} on screen “${screen.name || screen.id}”.`
          )
        );
      } else {
        positions.add(pos);
      }

      if (!(btn.label || '').trim()) {
        warnings.push(issue('EMPTY_LABEL', `${bp}.label`, 'Button label is empty.', 'warning'));
      } else if ((btn.label || '').length > limits.MAX_BUTTON_LABEL_CHARS) {
        warnings.push(
          issue(
            'LABEL_MAY_TRUNCATE',
            `${bp}.label`,
            `Label is ${(btn.label || '').length} characters; firmware truncates around ${limits.MAX_BUTTON_LABEL_CHARS}.`,
            'warning'
          )
        );
      }

      const actionType = btn.action?.type || PANEL_ACTION_NONE;
      const config = btn.action?.config || {};

      if (actionType === PANEL_ACTION_OPEN_SCREEN) {
        const target = config.targetScreenId;
        if (!target || !screenIds.has(target)) {
          // screenIds may be incomplete mid-loop; check after — collect for second pass
        }
        if (!target) {
          errors.push(
            issue(
              'SCREEN_TARGET_MISSING',
              `${bp}.action.config.targetScreenId`,
              'Target screen does not exist.'
            )
          );
        }
      } else if (
        actionType === PANEL_ACTION_HOME ||
        actionType === PANEL_ACTION_BACK ||
        actionType === PANEL_ACTION_NONE
      ) {
        const keys = Object.keys(config).filter((k) => config[k] != null && config[k] !== '');
        if (keys.length > 0) {
          warnings.push(
            issue(
              'UNEXPECTED_ACTION_CONFIG',
              `${bp}.action.config`,
              `${actionType} action should have empty config.`,
              'warning'
            )
          );
        }
      } else if (actionType === PANEL_ACTION_FLOW) {
        if (!config.flowId) {
          errors.push(
            issue(
              'FLOW_ID_REQUIRED',
              `${bp}.action.config.flowId`,
              'Flow action requires flowId.'
            )
          );
        }
      } else {
        errors.push(
          issue('UNKNOWN_ACTION', `${bp}.action.type`, `Unknown action type “${actionType}”.`)
        );
      }

      const visual = btn.visual || {};
      ['iconBackgroundColor', 'buttonBackgroundColor', 'textColor', 'iconColor'].forEach((field) => {
        if (!isValidColor(visual[field])) {
          errors.push(
            issue('INVALID_COLOR', `${bp}.visual.${field}`, `Invalid color “${visual[field]}”.`)
          );
        }
      });

      const iconType = visual.iconType || 'builtin';
      if (!['builtin', 'text'].includes(iconType)) {
        errors.push(
          issue('INVALID_ICON_TYPE', `${bp}.visual.iconType`, `Unsupported iconType “${iconType}”.`)
        );
      }
      if (iconType === 'builtin' && visual.iconKey && !isFirmwareSupportedIconKey(visual.iconKey)) {
        warnings.push(
          issue(
            'UNSUPPORTED_ICON_KEY',
            `${bp}.visual.iconKey`,
            `Icon “${visual.iconKey}” is not in the curated panel icon library; device will show a symbol fallback.`,
            'warning'
          )
        );
      }
    });
  });

  // Second pass: openScreen targets & global button id uniqueness
  const allButtonIds = new Set();
  let totalButtons = 0;
  screens.forEach((screen, si) => {
    (screen.buttons || []).forEach((btn, bi) => {
      totalButtons += 1;
      const bp = `panel.screens[${si}].buttons[${bi}]`;
      if (btn?.id) {
        if (allButtonIds.has(btn.id)) {
          errors.push(issue('DUPLICATE_BUTTON_ID', `${bp}.id`, `Duplicate button id “${btn.id}”.`));
        } else {
          allButtonIds.add(btn.id);
        }
      }
      if (btn?.action?.type === PANEL_ACTION_OPEN_SCREEN) {
        const target = btn.action?.config?.targetScreenId;
        if (target && !screenIds.has(target)) {
          errors.push(
            issue(
              'SCREEN_TARGET_MISSING',
              `${bp}.action.config.targetScreenId`,
              'Target screen does not exist.'
            )
          );
        }
      }
    });
  });

  if (totalButtons > limits.MAX_BUTTONS_TOTAL) {
    errors.push(
      issue(
        'TOO_MANY_BUTTONS',
        'panel.screens',
        `Panel has ${totalButtons} buttons; max is ${limits.MAX_BUTTONS_TOTAL} (provisional).`
      )
    );
  }

  if (!panel.defaultScreenId || !screenIds.has(panel.defaultScreenId)) {
    errors.push(
      issue(
        'DEFAULT_SCREEN_MISSING',
        'panel.defaultScreenId',
        'defaultScreenId must reference an existing screen.'
      )
    );
  }

  if (!isValidColor(panel.appearance?.backgroundColor)) {
    errors.push(
      issue(
        'INVALID_COLOR',
        'panel.appearance.backgroundColor',
        `Invalid color “${panel.appearance?.backgroundColor}”.`
      )
    );
  }

  const sizeBytes = measurePayloadBytes(payload);
  if (sizeBytes > limits.MAX_PAYLOAD_BYTES) {
    errors.push(
      issue(
        'PAYLOAD_TOO_LARGE',
        '',
        `Payload is ${sizeBytes} bytes; production max is ${limits.MAX_PAYLOAD_BYTES}.`
      )
    );
  }

  // Device compatibility
  if (device) {
    const schemaVersions =
      device.rendererSchemaVersions ||
      device.capabilities?.touchPanelSchemaVersions ||
      device.touchPanelSchemaVersions ||
      null;

    if (Array.isArray(schemaVersions) && schemaVersions.length > 0) {
      if (!schemaVersions.map(Number).includes(schemaVersion)) {
        errors.push(
          issue(
            'DEVICE_SCHEMA_INCOMPATIBLE',
            'schemaVersion',
            `Device does not support schema version ${schemaVersion}.`
          )
        );
      }
    } else if (device.compatibilityStatus === 'unknown') {
      warnings.push(
        issue(
          'DEVICE_SCHEMA_UNKNOWN',
          '',
          'Device schema support is unknown; preparation allowed with warning.',
          'warning'
        )
      );
    }

    const width = Number(device.displayWidth ?? device.display?.width);
    const height = Number(device.displayHeight ?? device.display?.height);
    if (Number.isFinite(width) && Number.isFinite(height)) {
      if (
        width < limits.MIN_DISPLAY_WIDTH ||
        width > limits.MAX_DISPLAY_WIDTH ||
        height < limits.MIN_DISPLAY_HEIGHT ||
        height > limits.MAX_DISPLAY_HEIGHT
      ) {
        errors.push(
          issue(
            'DEVICE_DISPLAY_INCOMPATIBLE',
            '',
            `Device display ${width}×${height} is outside provisional supported range.`
          )
        );
      }
    } else if (device.compatibilityStatus === 'unknown') {
      warnings.push(
        issue(
          'DEVICE_DISPLAY_UNKNOWN',
          '',
          'Device display size unknown; using provisional defaults for eligibility.',
          'warning'
        )
      );
    }

    if (device.compatibilityStatus === 'incompatible') {
      errors.push(
        issue(
          device.notFound || device.error === 'Device not found in agent list'
            ? 'DEVICE_NOT_FOUND'
            : 'DEVICE_INCOMPATIBLE',
          '',
          device.notFound || device.error === 'Device not found in agent list'
            ? 'Assigned device was not found in the agent list. Refresh and try again.'
            : 'Device is marked incompatible for Touch Panels.'
        )
      );
    }
  }

  // Deduplicate identical error codes/paths from first+second pass
  const seen = new Set();
  const uniqueErrors = [];
  errors.forEach((e) => {
    const key = `${e.code}|${e.path}|${e.message}`;
    if (seen.has(key)) return;
    seen.add(key);
    uniqueErrors.push(e);
  });

  return {
    valid: uniqueErrors.length === 0,
    errors: uniqueErrors,
    warnings,
    summary: summarizePayload(payload),
  };
}
